<template>
    <LoadingView v-if="loading" />
    <div v-if="!loading" class="flex flex-col gap-8">
        <nav class="flex flex-none gap-4 justify-between items-center rounded-t-lg">
            <div class="flex-none">
                <FormButton
                    color="secondary"
                    icon="fa-chevron-left"
                    @click="$router.go(-1)">
                    Back
                </FormButton>
            </div>
            <div v-if="this.profile" class="py-4 sm:rounded-tr-lg flex justify-end items-center h-16 gap-2">
                <FormButton v-if="profile" icon="fa-arrow-left-to-line" color="secondary" @click.prevent="logout()">Sign Out</FormButton>
            </div>
        </nav>

        <header class="flex items-center gap-8">
            <div class="flex flex-1 gap-3">
                <div class="flex-shrink-0 rounded-full overflow-hidden flex items-center justify-center bg-bhav-blue-400 text-white h-20 w-20 text-2xl">
                    <img v-if="item.image" class="aspect-square object-cover"
                    :src="item.image" :alt="item.name">
                    <span v-else>{{ (item.first_name?.[0] || '') + (item.last_name?.[0] || '') }}</span>
                </div>
                <div id="content" class="flex flex-col justify-center pt-3 pb-3 h-20">
                    <h3 class="text-bhav-blue-500 text-2xl leading-9 font-medium font-headline self-end flex items-end">{{ item.first_name ?? '' }} {{ item.last_name ?? '' }}</h3>
                    <span class="text-bhav-blue-400 text-lg font-medium self-start flex items-start">{{ UserLevels.levels[item.level ?? 0].label }}</span>
                </div>
            </div>
        </header>

        <div class="relative">
            <div v-if="selectedTab === 'notification-settings'" class="absolute top-4 right-10 z-10 pb-8 pl-8 pr-10 -mr-10">
                <FormButton color="blue" icon="fa-plus" prefix-class="fak" @click="showNewNotification">
                    New Notification
                </FormButton>
            </div>
            <TabPanes route="profile-tab" :tabs="tabs" @tab-change="selectedTab = $event" v-slot="{tab}" via="via-white" to="to-white" class="gap-4 py-6">
                <EditView v-if="tab.name === 'edit'" :id="this.id" :profile="this.profile" :item="this.item" :loading="this.loading" @update-user="updateUser" />
                <NotificationEditView v-if="tab.name === 'notification-settings'" ref="notificationEditView" :id="this.id" :profile="this.profile" :item="this.item" :loading="this.loading" />
            </TabPanes>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import LoadingView from '@/components/LoadingView.vue';
import { FormButton, SelectField } from '@/components/form';
import { useAuthStore } from '@/stores/auth';
import { mapActions, mapState } from 'pinia';
import { handle404 } from '@/helpers/globals';
import UserLevels from '@/enums/UserLevels';
import User from '@/models/user';
import NotificationEditView from '../notifications/EditView.vue';
import EditView from '../users/EditView.vue';
import TabPanes from '@/components/TabPanes.vue';

export default {
    head() {
        return {
            title: () => `${this.$route.meta.title} %separator ${this.item?.first_name} ${this.item?.last_name}`
        }
    },
    setup() {
        return {
            UserLevels
        }
    },
    data() {
        return {
            id: null,
            profile: false,
            selectedTab: 'edit',
            loading: true,
            item: new User
        }
    },
    methods: {
        ...mapActions(useAuthStore, ['logout']),
        load() {
            switch(this.$route.name) {
                case 'user-edit' :
                    this.id = this.$route.params.id;
                    this.profile = false;
                    break;
                case 'profile-tab' :
                    this.id = this.user.id;
                    this.profile = true;
                    break;
                default:
                    this.id = null;
                    this.profile = false;
                    break;
            }
            if(this.id) {
                this.show();
            } else {
                this.resetForm();
                this.loading = false;
            }
        },
        show() {
            if(!this.id) return;
            if(!this.profile) {
                axios.get(`users/${this.id}`).then(resp => {
                    this.item = new User(resp.data);
                    this.loading = false;
                })
                .catch(err => {
                    if(err.response.status === 404)
                        handle404(this, err.response.data.message);
                });
            } else {
                this.item = this.user;
                this.loading = false;
            }
        },
        resetForm() {
            this.item = {
                first_name: null,
                last_name: null,
                email: null,
                password: null,
                level: 1
            };
        },
        updateUser(updatedItem) {
            this.item = updatedItem;
        },
        showNewNotification() {
            // Emit event to NotificationEditView to show the new notification component
            this.$refs.notificationEditView?.showNewNotificationForm();
        }
    },
    computed: {
        ...mapState(useAuthStore, ['user']),
        tabs() {
            return [
                {
                    name: 'edit',
                    title: `${this.id ? 'Edit' : 'Add'} ${this.profile ? 'My Profile' : 'User'}`
                },
                {
                    name: 'notification-settings',
                    title: 'Notification Settings'
                }
            ].filter(tab => {
                if(!this.profile && (tab.name == 'notification-settings')) return false;
                return true;
            });
        },
        valid() {
            let required = this.item.first_name && this.item.last_name && this.item.email;
            if(!this.id) required = required && this.item.password;
            if(this.user.level !== null) required = required && this.item.level !== null;
            return required;
        }
    },
    mounted() {
        this.load();
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            vm.load();
        })
    },
    components: {
        LoadingView, FormButton, SelectField, TabPanes, NotificationEditView, EditView
    }
}
</script>